// Impersonation (docs/impersonation-plan.md).
//
// Communico staff open a library's COS as one of that library's own users,
// for support and training. The rebuild inverts the removed control-panel
// version's model: the real bearer token is NEVER replaced. Instead this
// module holds the id of a server-side `impersonation_sessions` row in
// sessionStorage and sends it as `X-COS-Impersonate` on every request (see
// services/api.ts) — the caller's own token, unchanged, is what actually
// authenticates the request; the header just tells the backend which
// effective identity to apply on top of it.
//
// sessionStorage (not localStorage) for the same reason legacy-client.ts
// uses it: tab-scoped, so a fresh tab starts with no session, and it does
// NOT survive a closed tab/window — appropriate for a support session that
// is time-boxed to begin with (30 minutes, server-enforced).
//
// Module-level singleton cache for the capability check (canImpersonate),
// mirroring services/community-context.ts / services/circulate-products.ts.

import { api } from "./api.js";

export interface ImpersonationTarget {
	id: number;
	name: string;
	email: string;
	role: string;
	level: number;
}

export type ImpersonationWriteMode = "read" | "write";

export interface ImpersonationSession {
	id: string;
	target: ImpersonationTarget;
	partner_id: number;
	write_mode: ImpersonationWriteMode;
	expires_at: string; // ISO 8601
}

export interface ImpersonationCandidate {
	id: number;
	name: string;
	email: string;
	role: string;
	level: number;
}

const STORE_KEY = "cos.impersonation.session";

/** Fired on every local session change (start / stop / allow-writes /
 *  refresh) — detail: { session: ImpersonationSession | null }. Both
 *  cos-app-shell (the avatar menu) and cos-impersonation-banner listen so
 *  neither has to poll the other's state. */
export const IMPERSONATION_EVENT = "cos:impersonation-changed";

/** The header api.ts attaches to every request while a session is held.
 *  Kept here (not duplicated in api.ts) so there is one place that names
 *  it — see the module doc comment above. */
export const IMPERSONATION_HEADER = "X-COS-Impersonate";

/** The machine-readable 403 the backend returns when a write is attempted
 *  under a read-only session (enforced server-side, never by hiding
 *  buttons — docs/impersonation-plan.md "Read-only enforcement"). */
export const IMPERSONATION_READONLY_CODE = "impersonation_read_only";

/** The machine-readable 403 the backend returns when the header names a
 *  session that is no longer live — stopped, expired, or never valid for
 *  this caller.
 *
 *  Deliberately NOT a 401 on either side. A dead session says nothing
 *  about the operator's own login, but api.ts treats 401 as "token bad":
 *  refresh, and on failure clear the token and bounce to /login. Any
 *  request already in flight when Stop lands still carries the old
 *  header, so this fires routinely — signing someone out of COS because
 *  the session they just ended has ended would be absurd. */
export const IMPERSONATION_ENDED_CODE = "impersonation_ended";

/** Broadcast by api.ts the moment ANY request comes back with the
 *  read-only code — the request that gets blocked could be triggered by
 *  any page in the app, not just something this module or the dialog
 *  started directly, so this can't be a plain returned value from one
 *  call site. detail: { path: string }. */
export const IMPERSONATION_READONLY_EVENT = "cos:impersonation-readonly-blocked";

function readStored(): ImpersonationSession | null {
	try {
		const raw = sessionStorage.getItem(STORE_KEY);
		if (!raw) return null;
		return JSON.parse(raw) as ImpersonationSession;
	} catch {
		return null;
	}
}

function writeStored(session: ImpersonationSession | null): void {
	try {
		if (session) sessionStorage.setItem(STORE_KEY, JSON.stringify(session));
		else sessionStorage.removeItem(STORE_KEY);
	} catch {
		/* not sticky, but the event still fires below — legacy-client's
		   pickLegacyClient() posture */
	}
}

function emitChange(session: ImpersonationSession | null): void {
	window.dispatchEvent(new CustomEvent(IMPERSONATION_EVENT, { detail: { session } }));
}

/** Synchronous read of the locally-held session — the fast path api.ts's
 *  request() consults on every call to build the header. Never triggers a
 *  fetch, so it's safe to call on every single request. */
export function currentImpersonationSession(): ImpersonationSession | null {
	return readStored();
}

/** The value to send as X-COS-Impersonate, or null when no session is held. */
export function impersonationHeaderValue(): string | null {
	return readStored()?.id ?? null;
}

/** That library's staff, for the "pick a user" list — always fetched via
 *  the search endpoint (the dialog always renders the search field, even
 *  under ten users, so there's exactly one code path regardless of size). */
export async function searchImpersonationCandidates(q: string): Promise<ImpersonationCandidate[]> {
	const res = (await api.getImpersonationCandidates(q)) as { users?: ImpersonationCandidate[] };
	return res.users ?? [];
}

/**
 * Starts a session against `targetUserId`. Sessions always start read-only
 * server-side (docs/impersonation-plan.md D1 / the migration's
 * write_mode DEFAULT 'read') — when the dialog's own "allow changes"
 * choice is on, this immediately follows the start with the same
 * allow-writes call a live session would use later, so the caller only
 * ever has to supply ONE reason regardless of which path elevates the
 * session. Throws (never silently no-ops) on any failure — the caller
 * (the dialog) is expected to surface it inline, per the "reload only on
 * confirmed success" flow in the plan.
 */
export async function startImpersonation(
	targetUserId: number,
	reason: string,
	allowWrites: boolean,
): Promise<ImpersonationSession> {
	// One request, not two: the server accepts write_mode on start, so an
	// operator who chose "allow changes" up front never passes through a
	// brief read-only window (and never leaves a half-started session
	// behind if the second call fails).
	const session = (await api.startImpersonation(
		targetUserId,
		reason,
		allowWrites,
	)) as ImpersonationSession;
	writeStored(session);
	emitChange(session);
	return session;
}

/**
 * Elevates the LIVE session to write mode with a reason — the explicit,
 * audited act the plan requires (never implicit, never silent). Used both
 * by the dialog's own toggle-at-start-time path (via startImpersonation
 * above) and by the banner's "allow changes" prompt shown after a
 * read-only write gets refused.
 */
export async function allowImpersonationWrites(reason: string): Promise<ImpersonationSession> {
	const session = (await api.allowImpersonationWrites(reason)) as ImpersonationSession;
	writeStored(session);
	emitChange(session);
	return session;
}

/**
 * Ends the live session, server-side first (so the row records how it
 * ended) then locally. Best-effort on the local clear even if the server
 * call fails — a session that's already gone (expired, ended elsewhere)
 * must not leave a stale id sitting in this tab regardless.
 */
export async function stopImpersonation(): Promise<void> {
	try {
		await api.stopImpersonation();
	} finally {
		writeStored(null);
		emitChange(null);
	}
}

/**
 * Reconciles the locally-held session against the server — for a fresh
 * tab (target=_blank doesn't inherit sessionStorage — see
 * legacy-client.ts's own note on this) or a session that expired/was
 * ended while this tab was inactive. Best-effort: a failed check leaves
 * whatever was already stored rather than clearing it, so a transient
 * network blip can't make an otherwise-live banner disappear.
 */
export async function refreshImpersonationSession(): Promise<ImpersonationSession | null> {
	try {
		const session = ((await api.getCurrentImpersonation()) ?? null) as ImpersonationSession | null;
		writeStored(session);
		emitChange(session);
		return session;
	} catch {
		return readStored();
	}
}

/**
 * Local-only clear — called from api.ts's clearToken() (sign-out, and the
 * 401-expired path). Deliberately never calls the backend: the token that
 * would authorize a stop-session request is going away in the very same
 * breath, and the server-side session is time-boxed regardless (30
 * minutes). Leaving the id in sessionStorage for the next person to reuse
 * this tab would be exactly the stale-session-in-a-shared-browser class
 * of bug legacy-client.ts's clearLegacyClient() exists to avoid.
 */
export function clearImpersonationSession(): void {
	writeStored(null);
	emitChange(null);
}

// The avatar-menu capability gate (D2: an explicit per-user grant, never
// inferred from level alone). One cached fetch per session, mirroring
// services/community-context.ts exactly: the raw fetch is cached but a
// FAILURE clears the cache so the next caller retries (a transient error
// must not permanently hide the menu item for the rest of the session);
// canImpersonate() itself degrades to false on any error or absent field
// — a menu item that fails open would be the one part of this feature
// that must never happen.
let _capabilityCache: Promise<{ can_impersonate?: boolean }> | null = null;

/** Test-only — mirrors legacy-client.ts's _resetForTests, so a case can
 *  observe an uncached fetch regardless of run order. */
export function _resetCanImpersonateForTests(): void {
	_capabilityCache = null;
}

function loadCapability(force = false): Promise<{ can_impersonate?: boolean }> {
	if (force || !_capabilityCache) {
		_capabilityCache = (api.getMe() as Promise<{ can_impersonate?: boolean }>).catch((e) => {
			_capabilityCache = null; // don't cache failures — the next caller retries
			throw e;
		});
	}
	return _capabilityCache;
}

export async function canImpersonate(force = false): Promise<boolean> {
	try {
		const me = await loadCapability(force);
		return me?.can_impersonate === true;
	} catch {
		return false;
	}
}
